import { useState, useRef } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/** Convert a stored upload path or URL to an absolute, browser-loadable URL. */
export function fileUrl(value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/api/")) return `${API_BASE}${value}`;
  return value;
}

/**
 * UploadButton — uploads a single file to /api/admin/upload, calls onUploaded(url).
 * Props:
 *   kind: "image" | "video"
 *   accept: input accept attribute
 *   onUploaded(url)
 *   label?: button label
 */
export default function UploadButton({ kind = "image", accept, onUploaded, label }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const defaultAccept = kind === "video" ? "video/mp4,video/webm,video/quicktime" : "image/png,image/jpeg,image/webp,image/gif";

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(r.data.url);
      toast.success(`${kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept || defaultAccept}
        onChange={onChange}
        className="hidden"
        data-testid={`upload-input-${kind}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="btn-secondary text-xs"
        data-testid={`upload-btn-${kind}`}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {label || (kind === "video" ? "Upload Video" : "Upload Image")}
      </button>
    </>
  );
}
